import { inject, Injectable } from "@angular/core";

import { Observable } from "rxjs";

import { environment } from "@environments/environment";
import { CacheId } from "@shared/enums";
import { HiRecordResponse, ParsedHiRecord, RequestConfig, RequestState } from "@shared/models";
import { ApiCacheService } from "@shared/services/util/api-cache.service";
import { convertDecimalToTime, convertToMinutes } from "@shared/functions/time.functions";
import { getContractualMonth } from "@shared/functions/date.functions";

@Injectable({
  providedIn: "root",
})
export class HiRecordsService {
  apiCacheService = inject(ApiCacheService);

  private readonly _baseUrl = `${environment.myTripApiUrl}/Activity`;
  private readonly _hi1Url = `${this._baseUrl}/hi1`;
  private readonly _hi2Url = `${this._baseUrl}/hi2`;
  private readonly _hi1RequestConfig: RequestConfig = {
    cacheId: CacheId.Hi1,
    cacheValidMinutes: 15,
    url: this._hi1Url,
    forceRefresh: false,
  };
  private readonly _hi2RequestConfig: RequestConfig = {
    cacheId: CacheId.Hi2,
    cacheValidMinutes: 15,
    url: this._hi2Url,
    forceRefresh: false,
  };

  getHiRecord(month: "current" | "next", forceRefresh: boolean): Observable<RequestState<HiRecordResponse>> {
    const requestConfig = month === "current" ? this._hi1RequestConfig : this._hi2RequestConfig;
    requestConfig.forceRefresh = forceRefresh;
    return this.apiCacheService.request<HiRecordResponse>(requestConfig);
  }

  // getHi1Record(forceRefresh: boolean): Observable<RequestState<HiRecordResponse>> {
  //   this._hi1RequestConfig.forceRefresh = forceRefresh;
  //   return this.apiCacheService.request<HiRecordResponse>(this._hi1RequestConfig);
  // }
  // getHi2Record(forceRefresh: boolean): Observable<RequestState<HiRecordResponse>> {
  //   this._hi2RequestConfig.forceRefresh = forceRefresh;
  //   return this.apiCacheService.request<HiRecordResponse>(this._hi2RequestConfig);
  // }

  parseHiRecord(record: string, monthType: "current" | "next"): ParsedHiRecord {
    const monthTypeOffset = monthType === "current" ? 0 : 1;
    const monthTypeFosEnd = getContractualMonth({ offset: monthTypeOffset }).monthShort;
    const getMatchValue = (regex: RegExp, text: string): string | null => {
      const match = text.match(regex);
      return match ? match[1] : null;
    };

    const proj = getMatchValue(/PROJ\s+(\d+\.\d+)/, record);
    const pproj = getMatchValue(/PPROJ\s+(\d+\.\d+)/, record);
    const ipmax = getMatchValue(/IPMAX\s+(\d+\.\d+)/, record);
    const vmax = getMatchValue(/VMAX\s+(\d+\.\d+)/, record) || (record.match(/VMAX FAR LIMIT/) ? "FAR" : "");
    let prelimAward = !!record.match(/PRELIMINARY AWARD-SUBJECT TO CHANGE/);
    const contractMonthEnd = getMatchValue(/MONTH ENDING (\d{2}[A-Z]{3})/, record);
    const month = contractMonthEnd ? getContractualMonth({ fosEnd: contractMonthEnd }).monthShort : null;
    const convertedProj = proj ? convertDecimalToTime(proj) : "";
    const convertedPproj = pproj ? convertDecimalToTime(pproj) : "";
    const convertedIpmax = ipmax ? convertDecimalToTime(ipmax) : "";
    const convertedVmax = vmax === "FAR" ? "FAR" : vmax ? convertDecimalToTime(vmax) : "";

    if (month !== monthTypeFosEnd) {
      prelimAward = true;
    }

    return {
      proj: convertToMinutes(convertedProj),
      pproj: convertToMinutes(convertedPproj),
      imax: convertToMinutes(convertedIpmax),
      vmax: convertToMinutes(convertedVmax),
      prelimAward,
      raw: record,
    };
  }
}
